import { notification } from "@excent-one/react/notification";
import { capitalCase } from "text-case";
import { FormFor } from "@excent-one/react/form";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderBackNavigation,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
} from "@excent-one/react/layout/content";
import { useRouteHandle } from "@excent-one/react/routing/useRouteHandle";
import {
  getIncidentRecordById,
  submitIncidentRecords,
  updateIncidentRecord,
} from "@phrmpc/infrastructure/phrmpc/api/incident-records";
import { getAllIncidentStatuses } from "@phrmpc/infrastructure/phrmpc/api/incident-statuses";
import { type IncidentRecordDto } from "@phrmpc/infrastructure/phrmpc/models";
import type { IncidentFormValues } from "../types";
import IncidentHeaderSection from "../components/IncidentHeaderSection";
import IncidentDetailsHeaderSection from "../components/IncidentDetailsHeaderSection";
import InvolvedMemberSection from "../components/InvolvedMemberSection";
import IncidentNarrativeSection from "../components/IncidentNarrativeSection";
import MonitoringCaseSection from "../components/MonitoringCaseSection";
import IncidentFormActions, {
  getStatusConfig,
} from "../components/IncidentFormActions";
import { IncidentRecordFileProvider } from "../components/IncidentRecordFileContext";
import {
  IncidentMonitoringGatingContext,
  defaultGating,
  type IncidentMonitoringGating,
} from "../components/IncidentMonitoringGatingContext";
import { dialogBox } from "@excent-one/react/dialog-box";

const defaultValues: IncidentFormValues = {
  incidentNumber: "",
  incidentTypeName: "",
  statusName: "",
  cycleAge: "",
  dateReported: undefined,
  dateOfIncident: undefined,
  venueOfIncident: "",
  timeOfIncident: undefined,
  reportedMemberId: undefined,
  reportedMemberDepartment: "",
  reportedMemberPosition: "",
  reportedMemberClient: "",
  reporterMemberId: undefined,
  reporterMemberDepartment: "",
  reporterMemberPosition: "",
  reporterMemberClient: "",
  incidentReportAttachment: null,
  incidentRecordFiles: [],
  incidentDescription: "",
  physicalEvidence: "",
  evidence: [],
  witnessDetails: "",
  moneyInvolved: "",
  decisionId: undefined,
  disciplinaryActionId: undefined,
  resignation: null,
  termination: null,
  remarks: "",
};

const { Form, useFormRef } = FormFor<IncidentFormValues>();

type SubmitIntent = "draft" | "submitIncident";

const IncidentForm = () => {
  const { id } = useParams({ strict: false });
  const { action } = useRouteHandle();
  const formRef = useFormRef();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const readOnly = action === "display";

  // Bug 157821: the "Submit" action used to bypass the <Form>'s own submit pipeline entirely -
  // a plain onClick handler that manually called trigger() (a no-op here, since this form has no
  // RHF resolver registered) then hit the submit endpoint directly, surfacing any server 400 as a
  // blanket "Unable to submit" toast instead of mapping field errors like Save already does.
  // Both Save and Submit now go through formRef.current.submit() - the <Form>'s real submit
  // handler, which calls this `submit` onSubmit and, on a thrown BadRequestError, maps the
  // server's field errors via setError automatically. This ref tells the single onSubmit which
  // endpoint/status transition the click intended, since RHF only supports one onSubmit per form.
  const submitIntentRef = useRef<SubmitIntent>("draft");

  const { data: statusOptions = [] } = useQuery({
    queryKey: ["incident-statuses"],
    queryFn: () => getAllIncidentStatuses(),
  });

  const {
    data: recordData,
    isLoading: recordLoading,
    isFetching: recordFetching,
  } = useQuery({
    queryKey: ["incident-record", id],
    queryFn: () => getIncidentRecordById(id!),
    enabled: Boolean(id),
  });

  const loading = recordLoading || recordFetching;

  const mapRecordToFormValues = useCallback(
    (record: IncidentRecordDto): IncidentFormValues => ({
      ...defaultValues,
      ...record,
      cycleAge: record.cycleAge != null ? `${record.cycleAge} day(s)` : "",
    }),
    [],
  );

  const initialValues = useMemo<IncidentFormValues>(() => {
    if (!recordData) return defaultValues;
    return mapRecordToFormValues(recordData);
  }, [recordData, mapRecordToFormValues]);

  const buildPayload = useCallback(
    (
      values: IncidentFormValues,
      statusIdOverride?: number,
    ): IncidentRecordDto => ({
      ...recordData,
      ...values,
      id,
      statusId: statusIdOverride ?? values.statusId,
      cycleAge: recordData?.cycleAge,
      remarks: values.remarks || undefined,
    }),
    [id, recordData],
  );

  const submit = useCallback(
    async (values: IncidentFormValues): Promise<IncidentFormValues> => {
      const intent = submitIntentRef.current;
      try {
        if (intent === "submitIncident") {
          const createdStatus = statusOptions.find((s) => s.name === "Created");
          const payload = buildPayload(values, createdStatus?.id);
          await submitIncidentRecords(id!, { id: id!, payload });
          notification.success("Incident is successfully created");
          // submitIncidentRecords returns void (no updated record echoed back), so nothing
          // updates the stale statusName ("Draft") carried over from the loaded form's values -
          // buildPayload only overrides statusId. Without this, the form resets back to
          // statusName: "Draft" despite the server-side transition succeeding, making the record
          // look like it's still a draft (same root cause class as Bug 157834's Save-path fix).
          const merged: IncidentRecordDto = {
            ...payload,
            id: id!,
            statusName: createdStatus?.name ?? payload.statusName,
          };
          queryClient.setQueryData(["incident-record", id], merged);
          await queryClient.invalidateQueries({
            queryKey: ["incident-records"],
          });
          if (!readOnly) {
            navigate({
              to: "/incident-management/$id/display",
              params: { id: id! },
            });
          }
          return mapRecordToFormValues(merged);
        }

        const payload = buildPayload(values);
        const updated = await updateIncidentRecord(id!, {
          id: id!,
          payload,
        });
        notification.success("Draft saved successfully.");
        // Bug 157834: the generic update endpoint's response doesn't echo back denormalized
        // fields like incidentTypeName or the computed cycleAge, so writing it straight into the
        // cache nulled out the page title and Header labels on Save. Fall back to the payload we
        // just sent (already merged with recordData via buildPayload) for anything the response
        // omits.
        const merged: IncidentRecordDto = {
          ...payload,
          ...updated,
          incidentTypeName:
            updated.incidentTypeName ?? payload.incidentTypeName,
          cycleAge: updated.cycleAge ?? payload.cycleAge,
        };
        queryClient.setQueryData(["incident-record", id], merged);
        await queryClient.invalidateQueries({ queryKey: ["incident-records"] });
        return mapRecordToFormValues(merged);
      } catch (e) {
        notification.error(
          intent === "submitIncident"
            ? "One or more validation errors occurred. Please review the highlighted fields."
            : "Unable to save draft. Please try again.",
        );
        throw e;
      }
    },
    [
      id,
      buildPayload,
      queryClient,
      mapRecordToFormValues,
      statusOptions,
      readOnly,
      navigate,
    ],
  );

  const triggerSubmit = useCallback(
    (intent: SubmitIntent) => {
      submitIntentRef.current = intent;
      formRef.current?.submit();
    },
    [formRef],
  );

  const onBack = useCallback(() => {
    if (!formRef.current) return;

    const proceedBack = () => navigate({ to: "/incident-management" });

    const hasUnsavedChanges =
      Object.keys(formRef.current.formState.dirtyFields).length > 0;

    if (hasUnsavedChanges) {
      dialogBox.confirm({
        title: "Leave without saving?",
        content: (
          <>
            Are you sure you want to go back? Any unsaved changes will be lost.
          </>
        ),
        actions: {
          confirm: { text: "Yes", onClick: proceedBack },
          cancel: { text: "No" },
        },
      });
    } else {
      proceedBack();
    }
  }, [navigate]);

  return (
    <ContentLayout>
      <ContentHeader loading={loading}>
        <ContentHeaderBackNavigation buttonProps={{ onClick: onBack }} />
        <ContentHeaderTitle
          title={`${capitalCase(action || "display")} ${initialValues.incidentTypeName} Incident Record`}
        />
        <ContentHeaderBreadcrumbs />
      </ContentHeader>

      <Form
        ref={formRef}
        onSubmit={submit}
        values={initialValues}
        defaultValues={defaultValues}
        errorRoot="payload"
      >
        {({ control, setValue, getValues }) => {
          const incidentTypeName = useWatch({
            control,
            name: "incidentTypeName",
          });
          const status = useWatch({ control, name: "statusName" });
          const statusConfig = getStatusConfig(status ?? "");

          const isMajorCase = incidentTypeName === "Major Case";
          const isMinorCase = incidentTypeName === "Minor Case";
          const isAwol = incidentTypeName === "AWOL";

          const showMonitorSection =
            action === "display" && statusConfig.showMonitoring;

          const mainFieldsReadOnly = readOnly || status !== "Draft";

          const gatingState = useState<IncidentMonitoringGating>(defaultGating);

          return (
            <IncidentRecordFileProvider
              incidentRecordId={id}
              setValue={setValue}
            >
              <IncidentMonitoringGatingContext.Provider value={gatingState}>
                <IncidentHeaderSection
                  title="Header"
                  loading={loading}
                  control={control}
                />

                <IncidentDetailsHeaderSection
                  title="Incident Form"
                  loading={loading}
                  control={control}
                  readOnly={mainFieldsReadOnly}
                  getValues={getValues}
                />

                <InvolvedMemberSection
                  title="Person to be Reported"
                  loading={loading}
                  control={control}
                  setValue={setValue}
                  readOnly={mainFieldsReadOnly}
                  idFieldName="reportedMemberId"
                  departmentFieldName="reportedMemberDepartment"
                  positionFieldName="reportedMemberPosition"
                  clientFieldName="reportedMemberClient"
                />

                <InvolvedMemberSection
                  title="Reported By"
                  loading={loading}
                  control={control}
                  setValue={setValue}
                  readOnly={mainFieldsReadOnly}
                  idFieldName="reporterMemberId"
                  departmentFieldName="reporterMemberDepartment"
                  positionFieldName="reporterMemberPosition"
                  clientFieldName="reporterMemberClient"
                />

                <IncidentNarrativeSection
                  title="Incident Details"
                  loading={loading}
                  control={control}
                  setValue={setValue}
                  incidentRecordId={id}
                  readOnly={mainFieldsReadOnly}
                />

                <MonitoringCaseSection
                  title={`Incident Report - ${isMajorCase ? "Major" : "Minor"} Case`}
                  hidden={!(showMonitorSection && (isMinorCase || isMajorCase))}
                  loading={loading}
                  control={control}
                  setValue={setValue}
                  incidentRecordId={id}
                  isAwol={false}
                />

                <MonitoringCaseSection
                  title="Incident Report - AWOL"
                  hidden={!(showMonitorSection && isAwol)}
                  loading={loading}
                  control={control}
                  setValue={setValue}
                  incidentRecordId={id}
                  isAwol
                />

                <IncidentFormActions
                  loading={loading}
                  readOnly={readOnly}
                  id={id}
                  status={status ?? ""}
                  showMonitorSection={showMonitorSection}
                  recordData={recordData}
                  getValues={getValues}
                  triggerSubmit={triggerSubmit}
                  buildPayload={buildPayload}
                  defaultValues={defaultValues}
                  isFormDirty={() =>
                    Object.keys(formRef.current?.formState.dirtyFields ?? {})
                      .length > 0
                  }
                />
              </IncidentMonitoringGatingContext.Provider>
            </IncidentRecordFileProvider>
          );
        }}
      </Form>
    </ContentLayout>
  );
};

export default IncidentForm;
